import { dataApi } from "./dataApi";
import { groupApi } from "./groupApi";
import { generateSchemaName, schemaApi, type CreateSchemaInput, type DatetimeStorage, type SchemaField, type SchemaFieldType } from "./schemaApi";
import type {
  CatalogSourceCandidate,
  DataSourceRef,
} from "@/types/componentDataBinding";

const CSV_IMPORT_TAG = "csv-import";
const DEFAULT_DATE_STORAGE: DatetimeStorage = {
  timezone: "utc",
  format: "iso-8601",
  hasTimezoneMarker: true,
};
const DELIMITER_CANDIDATES = [",", ";", "\t", "|"] as const;
const BOOLEAN_TRUE_VALUES = new Set(["true", "yes", "y"]);
const BOOLEAN_FALSE_VALUES = new Set(["false", "no", "n"]);
const NUMERIC_PATTERN = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
const DATE_PATTERN =
  /^(?:\d{4}-\d{2}-\d{2}(?:[ t]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:z|[+-]\d{2}:\d{2})?)?|\d{1,2}\/\d{1,2}\/\d{4}(?: \d{1,2}:\d{2}(?::\d{2})?)?)$/i;

type SupportedCsvFieldType = Extract<
  SchemaFieldType,
  "string" | "number" | "boolean" | "date"
>;

export interface CsvDatasetPreviewColumn {
  headerLabel: string;
  fieldName: string;
  type: SupportedCsvFieldType;
  required: boolean;
}

export interface CsvDatasetImportPreview {
  delimiter: string;
  rowCount: number;
  columns: CsvDatasetPreviewColumn[];
  sampleRows: string[][];
}

export interface CsvDatasetImportInput {
  file: File;
  datasetName: string;
  requestingOrganizationId: string;
  description?: string;
}

export interface CsvDatasetImportResult {
  source: DataSourceRef;
  candidate: CatalogSourceCandidate;
  insertedCount: number;
  preview: CsvDatasetImportPreview;
}

interface ImportedSchemaBinding {
  groupId: string;
  organizationId: string;
}

interface CsvColumnPlan {
  headerLabel: string;
  fieldName: string;
  type: SupportedCsvFieldType;
  required: boolean;
  rawValues: string[];
}

interface CsvDatasetDraft {
  preview: CsvDatasetImportPreview;
  fields: SchemaField[];
  records: Array<{
    data: Record<string, unknown>;
    metadata: {
      source: string;
      tags: string[];
    };
  }>;
}

const importedSchemaBindingCache = new Map<string, ImportedSchemaBinding>();

export function getImportedSchemaBinding(
  schemaId: string,
): ImportedSchemaBinding | undefined {
  return importedSchemaBindingCache.get(schemaId);
}

async function readFileText(file: File): Promise<string> {
  if ("text" in file && typeof file.text === "function") {
    return file.text();
  }

  if ("arrayBuffer" in file && typeof file.arrayBuffer === "function") {
    const buffer = await file.arrayBuffer();
    return new TextDecoder().decode(buffer);
  }

  if (typeof FileReader === "undefined") {
    throw new Error("Unable to read the selected CSV file.");
  }

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error("Unable to read the selected CSV file."));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
        return;
      }
      reject(new Error("Unable to read the selected CSV file."));
    };
    reader.readAsText(file);
  });
}

function requireNonEmptyString(value: unknown, fieldName: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${fieldName} is required.`);
  }

  return value.trim();
}

function normalizeDatasetDisplayName(datasetName: string, fileName: string): string {
  const trimmed = datasetName.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }

  const fileBaseName = fileName.replace(/\.[^.]+$/, "").trim();
  if (fileBaseName.length > 0) {
    return fileBaseName;
  }

  return "CSV Import";
}

function buildUniqueSchemaName(label: string): string {
  const suffix = Date.now().toString(36);
  const base = generateSchemaName(label);
  const safeBase =
    base.length > 0 && /^[a-z]/.test(base) ? base : `csv_import_${base}`.replace(/_+$/, "");
  return `${safeBase}_${suffix}`;
}

function buildUniqueGroupSlug(label: string): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const safeBase = base.length > 0 && /[a-z]/.test(base) ? base : "csv-import";
  return `${safeBase}-${Date.now().toString(36)}`;
}

function buildRetryGroupName(label: string): string {
  const suffix = Date.now().toString(36).slice(-4);
  return `${label} ${suffix}`.trim();
}

function errorIndicatesDuplicateGroupName(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const maybeMessage = Reflect.get(error, "message");
  if (typeof maybeMessage === "string" && maybeMessage.includes("already exists")) {
    return true;
  }

  const response = Reflect.get(error, "response");
  if (typeof response !== "object" || response === null) {
    return false;
  }

  const data = Reflect.get(response, "data");
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const responseMessage = Reflect.get(data, "message");
  return typeof responseMessage === "string" && responseMessage.includes("already exists");
}

function stripBom(value: string): string {
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}

function countColumnsForDelimiter(line: string, delimiter: string): number {
  let columns = 1;
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];

    if (character === '"') {
      if (inQuotes && line[index + 1] === '"') {
        index += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }

    if (!inQuotes && character === delimiter) {
      columns += 1;
    }
  }

  return columns;
}

export function detectCsvDelimiter(text: string): string {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
    .slice(0, 5);

  if (lines.length === 0) {
    return ",";
  }

  let bestDelimiter = ",";
  let bestScore = 0;

  for (const delimiter of DELIMITER_CANDIDATES) {
    const counts = lines.map((line) => countColumnsForDelimiter(line, delimiter));
    const firstCount = counts[0] ?? 0;
    const consistentCount = counts.filter((count) => count === firstCount && count > 1)
      .length;
    const score = firstCount * 10 + consistentCount;

    if (score > bestScore) {
      bestDelimiter = delimiter;
      bestScore = score;
    }
  }

  return bestDelimiter;
}

export function parseCsvText(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let currentValue = "";
  let currentRow: string[] = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (character === '"') {
      if (inQuotes && text[index + 1] === '"') {
        currentValue += '"';
        index += 1;
        continue;
      }

      inQuotes = !inQuotes;
      continue;
    }

    if (!inQuotes && character === delimiter) {
      currentRow.push(currentValue);
      currentValue = "";
      continue;
    }

    if (!inQuotes && (character === "\n" || character === "\r")) {
      if (character === "\r" && text[index + 1] === "\n") {
        index += 1;
      }
      currentRow.push(currentValue);
      rows.push(currentRow);
      currentRow = [];
      currentValue = "";
      continue;
    }

    currentValue += character;
  }

  if (currentValue.length > 0 || currentRow.length > 0) {
    currentRow.push(currentValue);
    rows.push(currentRow);
  }

  if (rows.length > 0 && rows[0] !== undefined && rows[0][0] !== undefined) {
    rows[0][0] = stripBom(rows[0][0]);
  }

  return rows.filter((row) => row.some((cell) => cell.trim().length > 0));
}

function isBooleanLiteral(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return BOOLEAN_TRUE_VALUES.has(normalized) || BOOLEAN_FALSE_VALUES.has(normalized);
}

function parseBooleanLiteral(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (BOOLEAN_TRUE_VALUES.has(normalized)) {
    return true;
  }
  if (BOOLEAN_FALSE_VALUES.has(normalized)) {
    return false;
  }

  throw new Error(`Invalid boolean literal: ${value}`);
}

function isNumericLiteral(value: string): boolean {
  return NUMERIC_PATTERN.test(value.trim());
}

function isDateLiteral(value: string): boolean {
  const trimmed = value.trim();
  return DATE_PATTERN.test(trimmed) && Number.isFinite(Date.parse(trimmed));
}

function normalizeFieldName(rawHeader: string, index: number, taken: Set<string>): string {
  const normalized = generateSchemaName(rawHeader.trim());
  const baseName =
    normalized.length > 0 && /^[a-z]/.test(normalized)
      ? normalized
      : `column_${index + 1}`;
  let uniqueName = baseName;
  let suffix = 2;

  while (taken.has(uniqueName)) {
    uniqueName = `${baseName}_${suffix}`;
    suffix += 1;
  }

  taken.add(uniqueName);
  return uniqueName;
}

function inferColumnType(values: string[]): SupportedCsvFieldType {
  const nonEmptyValues = values.map((value) => value.trim()).filter((value) => value.length > 0);

  if (nonEmptyValues.length === 0) {
    return "string";
  }
  if (nonEmptyValues.every(isBooleanLiteral)) {
    return "boolean";
  }
  if (nonEmptyValues.every(isNumericLiteral)) {
    return "number";
  }
  if (nonEmptyValues.every(isDateLiteral)) {
    return "date";
  }
  return "string";
}

function buildColumnPlans(rows: string[][]): CsvColumnPlan[] {
  if (rows.length < 2) {
    throw new Error("CSV import requires a header row and at least one data row.");
  }

  const maxColumnCount = rows.reduce(
    (max, row) => Math.max(max, row.length),
    0,
  );
  const headerRow = rows[0] ?? [];
  const dataRows = rows.slice(1);
  const takenFieldNames = new Set<string>();

  return Array.from({ length: maxColumnCount }, (_unused, index) => {
    const rawHeader = headerRow[index]?.trim() ?? "";
    const headerLabel = rawHeader.length > 0 ? rawHeader : `Column ${index + 1}`;
    const rawValues = dataRows.map((row) => row[index] ?? "");
    const required = rawValues.every((value) => value.trim().length > 0);
    const type = inferColumnType(rawValues);

    return {
      headerLabel,
      fieldName: normalizeFieldName(rawHeader, index, takenFieldNames),
      type,
      required,
      rawValues,
    };
  });
}

function normalizeCellValue(
  rawValue: string,
  type: SupportedCsvFieldType,
): unknown | undefined {
  const trimmed = rawValue.trim();

  if (trimmed.length === 0) {
    return undefined;
  }

  switch (type) {
    case "boolean":
      return parseBooleanLiteral(trimmed);
    case "number":
      return Number(trimmed);
    case "date":
      return new Date(trimmed).toISOString();
    case "string":
    default:
      return trimmed;
  }
}

function buildSchemaFields(columnPlans: CsvColumnPlan[]): SchemaField[] {
  return columnPlans.map((column) => ({
    id: `field_${column.fieldName}`,
    name: column.fieldName,
    type: column.type,
    required: column.required,
    description:
      column.headerLabel !== column.fieldName ? column.headerLabel : undefined,
    ...(column.type === "date" ? { storage: DEFAULT_DATE_STORAGE } : {}),
  }));
}

function buildDraftFromCsvText(text: string): CsvDatasetDraft {
  const delimiter = detectCsvDelimiter(text);
  const rows = parseCsvText(text, delimiter);
  const columnPlans = buildColumnPlans(rows);
  const dataRows = rows.slice(1);

  const records = dataRows.map((row) => {
    const data: Record<string, unknown> = {};

    for (const [columnIndex, column] of columnPlans.entries()) {
      const normalizedValue = normalizeCellValue(row[columnIndex] ?? "", column.type);
      if (normalizedValue !== undefined) {
        data[column.fieldName] = normalizedValue;
      }
    }

    return {
      data,
      metadata: {
        source: CSV_IMPORT_TAG,
        tags: [CSV_IMPORT_TAG],
      },
    };
  });

  return {
    preview: {
      delimiter,
      rowCount: records.length,
      columns: columnPlans.map((column) => ({
        headerLabel: column.headerLabel,
        fieldName: column.fieldName,
        type: column.type,
        required: column.required,
      })),
      sampleRows: dataRows.slice(0, 5).map((row) =>
        columnPlans.map((_column, columnIndex) => row[columnIndex] ?? ""),
      ),
    },
    fields: buildSchemaFields(columnPlans),
    records,
  };
}

export async function analyzeCsvDatasetFile(
  file: File,
): Promise<CsvDatasetImportPreview> {
  const text = await readFileText(file);
  return buildDraftFromCsvText(text).preview;
}

function getSourceIdentity(params: {
  requestingOrganizationId: string;
  schemaOrganizationId: string;
  groupOrganizationId: string;
  membershipOrganizationId: string;
  membershipGroupId: string;
  membershipSchemaId: string;
}): {
  dataOwnerOrganizationId: string;
  groupId: string;
  schemaId: string;
} {
  const requestingOrganizationId = requireNonEmptyString(
    params.requestingOrganizationId,
    "requestingOrganizationId",
  );
  const schemaOrganizationId = requireNonEmptyString(
    params.schemaOrganizationId,
    "schema.organizationId",
  );
  const groupOrganizationId = requireNonEmptyString(
    params.groupOrganizationId,
    "group.organizationId",
  );
  const membershipOrganizationId = requireNonEmptyString(
    params.membershipOrganizationId,
    "membership.organizationId",
  );

  if (
    schemaOrganizationId !== groupOrganizationId ||
    schemaOrganizationId !== membershipOrganizationId
  ) {
    throw new Error(
      "Created CSV dataset returned mismatched owner organization identity.",
    );
  }

  if (schemaOrganizationId !== requestingOrganizationId) {
    throw new Error(
      "Created CSV dataset did not match the explicit requesting organization.",
    );
  }

  return {
    dataOwnerOrganizationId: membershipOrganizationId,
    groupId: requireNonEmptyString(params.membershipGroupId, "membership.groupId"),
    schemaId: requireNonEmptyString(
      params.membershipSchemaId,
      "membership.schemaId",
    ),
  };
}

function buildCatalogCandidate(params: {
  source: DataSourceRef;
  groupName: string;
  groupSlug: string;
  schemaName: string;
  schemaDisplayName: string;
}): CatalogSourceCandidate {
  return {
    sourceKind: "schema",
    authorizationPath: "data-service:schema-group-membership",
    source: params.source,
    labels: {
      groupName: params.groupName,
      groupSlug: params.groupSlug,
      schemaName: params.schemaName,
      schemaDisplayName: params.schemaDisplayName,
    },
    capabilities: {
      readable: true,
      writable: true,
    },
  };
}

export async function importCsvDataset(
  input: CsvDatasetImportInput,
): Promise<CsvDatasetImportResult> {
  const requestingOrganizationId = requireNonEmptyString(
    input.requestingOrganizationId,
    "requestingOrganizationId",
  );
  const datasetDisplayName = normalizeDatasetDisplayName(
    input.datasetName,
    input.file.name,
  );
  const csvText = await readFileText(input.file);
  const draft = buildDraftFromCsvText(csvText);

  const schemaTemplate: CreateSchemaInput = {
    name: buildUniqueSchemaName(datasetDisplayName),
    displayName: datasetDisplayName,
    description:
      input.description?.trim() ||
      `Imported from CSV file ${input.file.name}.`,
    fields: draft.fields,
    visibility: "private",
    tags: [CSV_IMPORT_TAG],
  };

  const groupTemplate = {
    name: datasetDisplayName,
    slug: buildUniqueGroupSlug(datasetDisplayName),
    description:
      input.description?.trim() ||
      `CSV import dataset for ${datasetDisplayName}.`,
  };

  const scopeOptions = { organizationId: requestingOrganizationId };
  const createdSchema = await schemaApi.createSchema(schemaTemplate, scopeOptions);
  const schema = await schemaApi.publishSchema(createdSchema.id, scopeOptions);
  const group = await (async () => {
    try {
      return await groupApi.createGroup(groupTemplate, scopeOptions);
    } catch (error) {
      if (!errorIndicatesDuplicateGroupName(error)) {
        throw error;
      }

      const retryGroupName = buildRetryGroupName(datasetDisplayName);

      return groupApi.createGroup(
        {
          ...groupTemplate,
          name: retryGroupName,
        },
        scopeOptions,
      );
    }
  })();
  const membership = await groupApi.addSchemaToGroup(
    group.id,
    {
      schemaId: schema.id,
    },
    scopeOptions,
  );

  const identity = getSourceIdentity({
    requestingOrganizationId,
    schemaOrganizationId: schema.organizationId,
    groupOrganizationId: group.organizationId,
    membershipOrganizationId: membership.organizationId,
    membershipGroupId: membership.groupId,
    membershipSchemaId: membership.schemaId,
  });

  const source: DataSourceRef = {
    kind: "schema",
    dataOwnerOrganizationId: identity.dataOwnerOrganizationId,
    ownerType: "organization",
    ownerId: identity.dataOwnerOrganizationId,
    groupId: identity.groupId,
    schemaId: identity.schemaId,
  };

  importedSchemaBindingCache.set(source.schemaId, {
    groupId: source.groupId,
    organizationId: source.dataOwnerOrganizationId,
  });
  await dataApi.createRecords(
    source.groupId,
    source.schemaId,
    draft.records,
    {
      dataOwnerOrganizationId: source.dataOwnerOrganizationId,
    },
  );

  return {
    source,
    candidate: buildCatalogCandidate({
      source,
      groupName: group.name,
      groupSlug: group.slug,
      schemaName: schema.name,
      schemaDisplayName: schema.displayName,
    }),
    insertedCount: draft.records.length,
    preview: draft.preview,
  };
}

export { buildDraftFromCsvText };
